import {
  HIDDEN_PRODUCT_TAG,
  SHOP_CHAIR_COLLECTION,
  SHOP_DESK_COLLECTION,
  SHOPIFY_GRAPHQL_API_ENDPOINT,
} from "lib/constants";
import { ensureStartsWith } from "lib/utils";
import {
  buildVariantQueryData,
  withProductQueryData,
  withVariantQueryData,
} from "./product-query-data";
import {
  addToCartMutation,
  createCartMutation,
  editCartItemsMutation,
  removeFromCartMutation,
} from "./mutations/cart";
import { getCartQuery } from "./queries/cart";
import {
  getMetaobjectByHandleQuery,
  getMetaobjectsByIdsQuery,
  getMetaobjectsByTypeQuery,
  resolveFilesQuery,
} from "./queries/metaobjects";
import {
  getCollectionProductsQuery,
  getNavigationCollectionProductsQuery,
  getProductQuery,
  getRevolveCollectionVariantsQuery,
  getStoreCollectionProductsQuery,
} from "./queries/product";
import {
  Cart,
  Connection,
  Image,
  Product,
  RevolveCollectionVariant,
  ShopifyAddToCartOperation,
  ShopifyCart,
  ShopifyCartOperation,
  ShopifyCollectionProductsOperation,
  ShopifyCreateCartOperation,
  ShopifyProductOperation,
  ShopifyRevolveCollectionVariantsOperation,
  ShopifyStoreCollectionProductsOperation,
  ShopifyRemoveFromCartOperation,
  ShopifyUpdateCartOperation,
  StoreProductCardData,
} from "./types";

const domain = process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN
  ? ensureStartsWith(process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN, "https://")
  : "";
const endpoint = `${domain}${SHOPIFY_GRAPHQL_API_ENDPOINT}`;
const key = process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN!;

type ExtractVariables<T> = T extends { variables: object }
  ? T["variables"]
  : never;

export async function shopifyClientFetch<T>({
  query,
  variables,
  cache,
  revalidate,
  tags,
}: {
  query: string;
  variables?: ExtractVariables<T>;
  cache?: RequestCache;
  revalidate?: number;
  tags?: string[];
}): Promise<{ status: number; body: T } | never> {
  let attempt = 0;
  const retries = 3;

  while (attempt <= retries) {
    try {
      const result = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": key,
        },
        body: JSON.stringify({
          ...(query && { query }),
          ...(variables && { variables }),
        }),
        cache,
        ...(revalidate !== undefined && { next: { revalidate, tags } }),
      });

      const body = await result.json();

      if (body.errors) {
        console.error("Shopify Client API Error:", {
          query,
          variables,
          errors: body.errors,
        });
        throw body.errors[0]; // Throw GraphQL error
      }

      return {
        status: result.status,
        body,
      };
    } catch (e) {
      // TypeError is thrown by fetch on network failures (like DNS/ERR_NAME_NOT_RESOLVED)
      if (e instanceof TypeError) {
        attempt++;
        if (attempt > retries) {
          console.error(
            "Shopify Client Fetch Network Error Failed after retries:",
            {
              query,
              variables,
              error: e.message,
            },
          );

          fetch("/api/client-error", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              error: e.message,
              type: "ShopifyClientFetchNetworkError",
              query,
              variables,
            }),
          }).catch(console.error);

          throw e;
        }
        // Wait before retrying
        await new Promise((resolve) =>
          setTimeout(resolve, 500 * Math.pow(2, attempt - 1)),
        );
        continue;
      }

      // If it's not a network error (e.g. GraphQL error), throw immediately without retrying
      console.error("Shopify Client Fetch Failed:", {
        query,
        variables,
        error: e instanceof Error ? e.message : e,
      });
      throw e;
    }
  }
  throw new Error("Unexpected end of fetch loop");
}

export const reshapeCart = (cart: ShopifyCart): Cart => {
  if (!cart) {
    throw new Error("Cart is null or undefined");
  }

  if (cart.cost && !cart.cost.totalTaxAmount) {
    cart.cost.totalTaxAmount = {
      amount: "0.0",
      currencyCode: cart.cost.totalAmount.currencyCode,
    };
  }

  return {
    ...cart,
    lines: cart.lines.edges.map((edge) => edge?.node),
  };
};

export async function createCartClient(): Promise<Cart> {
  const res = await shopifyClientFetch<ShopifyCreateCartOperation>({
    query: createCartMutation,
  });
  return reshapeCart(res.body.data.cartCreate.cart);
}

export async function createCartWithLinesClient(
  lines: { merchandiseId: string; quantity: number }[],
): Promise<Cart> {
  const res = await shopifyClientFetch<ShopifyCreateCartOperation>({
    query: createCartMutation,
    variables: {
      lineItems: lines,
    },
  });
  return reshapeCart(res.body.data.cartCreate.cart);
}

export async function addToCartClient(
  cartId: string,
  lines: { merchandiseId: string; quantity: number }[],
): Promise<Cart> {
  const decodedCartId = decodeURIComponent(cartId);
  const res = await shopifyClientFetch<ShopifyAddToCartOperation>({
    query: addToCartMutation,
    variables: {
      cartId: decodedCartId,
      lines,
    },
  });
  return reshapeCart(res.body.data.cartLinesAdd.cart);
}

export async function removeFromCartClient(
  cartId: string,
  lineIds: string[],
): Promise<Cart> {
  const decodedCartId = decodeURIComponent(cartId);
  const res = await shopifyClientFetch<ShopifyRemoveFromCartOperation>({
    query: removeFromCartMutation,
    variables: {
      cartId: decodedCartId,
      lineIds,
    },
  });
  const cart = res.body.data.cartLinesRemove.cart;
  return cart ? reshapeCart(cart) : createCartClient();
}

export async function updateCartClient(
  cartId: string,
  lines: { id: string; merchandiseId: string; quantity: number }[],
): Promise<Cart> {
  const decodedCartId = decodeURIComponent(cartId);
  const res = await shopifyClientFetch<ShopifyUpdateCartOperation>({
    query: editCartItemsMutation,
    variables: {
      cartId: decodedCartId,
      lines,
    },
  });
  const cart = res.body.data.cartLinesUpdate.cart;
  return cart ? reshapeCart(cart) : createCartClient();
}

export async function getCartClient(cartId: string): Promise<Cart | undefined> {
  if (!cartId) return undefined;
  const decodedCartId = decodeURIComponent(cartId);
  const res = await shopifyClientFetch<ShopifyCartOperation>({
    query: getCartQuery,
    variables: { cartId: decodedCartId },
  });
  if (!res.body.data.cart) {
    return undefined;
  }
  return reshapeCart(res.body.data.cart);
}

const removeEdgesAndNodes = <T>(array: Connection<T>): T[] => {
  return array.edges.map((edge) => edge?.node);
};

const reshapeImages = (images: Connection<Image>, productTitle: string) => {
  const flattened = removeEdgesAndNodes(images);

  return flattened.map((image) => {
    const filename = image.url.match(/.*\/(.*)\..*/)?.[1];
    return {
      ...image,
      altText: image.altText || `${productTitle} - ${filename}`,
    };
  });
};

const reshapeProduct = (
  product: any, // Using any here because ShopifyProduct type might be slightly different in Storefront API vs Admin
  filterHiddenProducts: boolean = true,
) => {
  if (
    !product ||
    (filterHiddenProducts && product.tags?.includes(HIDDEN_PRODUCT_TAG))
  ) {
    return undefined;
  }

  const { images, variants, ...rest } = product;

  return withProductQueryData({
    ...rest,
    images: reshapeImages(images, product.title),
    variants: removeEdgesAndNodes(variants),
  } as Product);
};

export const getProductClient = cache(async function getProductClient(
  handle: string,
): Promise<Product | undefined> {
  const res = await shopifyClientFetch<ShopifyProductOperation>({
    query: getProductQuery,
    variables: {
      handle,
    },
    revalidate: 86400,
    tags: ["products", `product:${handle}`],
  });

  if (!res.body.data.product) return undefined;

  return reshapeProduct(res.body.data.product) as unknown as Product;
});

export async function getCollectionProductsClient({
  collection,
  reverse,
  sortKey,
}: {
  collection: string;
  reverse?: boolean;
  sortKey?: string;
}): Promise<Product[]> {
  const res = await shopifyClientFetch<ShopifyCollectionProductsOperation>({
    query: getCollectionProductsQuery,
    variables: {
      handle: collection,
      reverse,
      sortKey,
    },
    revalidate: 86400,
    tags: ["collections", `collection:${collection}`],
  });

  if (!res.body.data.collection) {
    console.log(`No collection found for \`${collection}\``);
    return [];
  }

  return reshapeProducts(
    removeEdgesAndNodes(res.body.data.collection.products),
  );
}

const reshapeProducts = (products: any[]) => {
  const reshapedProducts = [];

  for (const product of products) {
    if (product) {
      const reshapedProduct = reshapeProduct(product);

      if (reshapedProduct) {
        reshapedProducts.push(reshapedProduct);
      }
    }
  }

  return reshapedProducts as unknown as Product[];
};

const getCollectionSortKey = (handle: string) =>
  handle === SHOP_CHAIR_COLLECTION || handle === SHOP_DESK_COLLECTION
    ? "COLLECTION_DEFAULT"
    : "PRICE";

export async function fetchCollectionProductsClient(handle: string): Promise<
  {
    label: string;
    href: string;
    description: string;
  }[]
> {
  try {
    const products = await getCollectionProductsClient({
      collection: handle,
      sortKey: getCollectionSortKey(handle),
    });

    return products.slice(0, 10).map((product) => ({
      label: product.title,
      href: `/products/${product.handle}`,
      description:
        product.priceRange.minVariantPrice.amount !==
        product.priceRange.maxVariantPrice.amount
          ? `${product.priceRange.minVariantPrice.currencyCode} ${product.priceRange.minVariantPrice.amount} - ${product.priceRange.maxVariantPrice.amount}`
          : `${product.priceRange.minVariantPrice.currencyCode} ${product.priceRange.minVariantPrice.amount}`,
    }));
  } catch (error) {
    console.error("Failed to fetch collection products:", error);
    return [];
  }
}

export async function fetchStoreCollectionProductsClient(
  handle: string,
): Promise<StoreProductCardData[]> {
  try {
    const res =
      await shopifyClientFetch<ShopifyStoreCollectionProductsOperation>({
        query: getStoreCollectionProductsQuery,
        variables: {
          handle,
          sortKey: getCollectionSortKey(handle),
        },
        revalidate: 86400,
        tags: ["collections", `collection:${handle}`],
      });

    if (!res.body.data.collection) return [];

    return removeEdgesAndNodes(res.body.data.collection.products)
      .filter((product) => !product.tags?.includes(HIDDEN_PRODUCT_TAG))
      .map((product) => {
        const variants = removeEdgesAndNodes(product.variants);
        const colorVariants = new Map<
          string,
          StoreProductCardData["colorVariants"][number]
        >();

        variants.forEach((variant, variantIndex) => {
          const color =
            variant.selectedOptions.find(
              (option) => option.name.toLowerCase() === "color",
            )?.value || "Default";

          if (colorVariants.has(color)) return;

          const queryData = buildVariantQueryData(variant);
          colorVariants.set(color, {
            name: variant.title || color,
            color,
            primaryColor: queryData.primaryColor,
            accentColor: queryData.accentColor,
            video: queryData.media.videos[0] || null,
            images: queryData.media.images,
            storeCardMedia: queryData.storeCardMedia,
            hoverImage: queryData.hoverImage,
            variantIndex,
          });
        });

        const minimumPrice = Number.parseFloat(
          product.priceRange.minVariantPrice.amount,
        );

        return {
          id: product.id,
          handle: product.handle,
          title: product.title,
          minimumPrice: Number.isFinite(minimumPrice) ? minimumPrice : 0,
          currencyCode:
            product.priceRange.minVariantPrice.currencyCode || "INR",
          fallbackImage:
            product.featuredImage?.url ||
            removeEdgesAndNodes(product.images)[0]?.url ||
            "",
          isBestSeller:
            product.metafields?.find(
              (metafield) =>
                metafield?.namespace === "custom" &&
                metafield.key === "product_popularity_tag",
            )?.value === "true",
          colorVariants: Array.from(colorVariants.values()),
        };
      });
  } catch (error) {
    console.error("Failed to fetch store collection products:", error);
    return [];
  }
}

// Full products are still required by configurator-adjacent client flows that
// reuse the store card presentation and populate the product query cache.
export async function fetchCollectionWithFullProductsClient(
  handle: string,
): Promise<Product[]> {
  try {
    return await getCollectionProductsClient({
      collection: handle,
      sortKey: getCollectionSortKey(handle),
    });
  } catch (error) {
    console.error("Failed to fetch collection with full products:", error);
    return [];
  }
}

export async function fetchNavigationCollectionClient(
  handle: string,
): Promise<Product[]> {
  try {
    const res = await shopifyClientFetch<ShopifyCollectionProductsOperation>({
      query: getNavigationCollectionProductsQuery,
      variables: {
        handle,
        sortKey: getCollectionSortKey(handle),
      },
      revalidate: 86400,
      tags: ["navigation", `collection:${handle}`],
    });

    if (!res.body.data.collection) return [];

    const products = reshapeProducts(
      removeEdgesAndNodes(res.body.data.collection.products),
    );
    return products;
  } catch (error) {
    console.error("Failed to fetch navigation collection:", error);
    return [];
  }
}

export async function fetchRevolveCollectionVariantsClient(
  handle: string,
): Promise<RevolveCollectionVariant[]> {
  try {
    const res =
      await shopifyClientFetch<ShopifyRevolveCollectionVariantsOperation>({
        query: getRevolveCollectionVariantsQuery,
        variables: { handle },
        revalidate: 86400,
        tags: ["collections", `collection:${handle}`, "revolve"],
      });

    if (!res.body.data.collection) {
      console.log(`No collection found for \`${handle}\``);
      return [];
    }

    return removeEdgesAndNodes(res.body.data.collection.products)
      .filter((product) => !product.tags?.includes(HIDDEN_PRODUCT_TAG))
      .flatMap((product) => {
        const productSummary = {
          id: product.id,
          handle: product.handle,
          title: product.title,
          featuredImage: product.featuredImage,
          images: reshapeImages(product.images, product.title),
        };

        return removeEdgesAndNodes(product.variants).map((variant) => ({
          product: productSummary,
          variant: withVariantQueryData(variant),
        }));
      });
  } catch (error) {
    console.error("Failed to fetch Revolve collection variants:", error);
    return [];
  }
}

/**
 * Batch resolve Shopify file and media IDs to their URLs using Storefront API
 */
export async function resolveFileIdsClient(
  fileIds: string[],
): Promise<Record<string, string | null>> {
  if (fileIds.length === 0) return {};

  try {
    const res = await shopifyClientFetch<any>({
      query: resolveFilesQuery,
      variables: { ids: fileIds },
    });

    const fileMap: Record<string, string | null> = {};
    const nodes = res.body.data?.nodes || [];

    nodes.forEach((item: any) => {
      if (!item) return;
      if (item.image?.url) {
        fileMap[item.id] = item.image.url;
      } else if (item.url) {
        fileMap[item.id] = item.url;
      } else if (item.sources && item.sources.length > 0) {
        fileMap[item.id] = item.sources[0].url;
      }
    });

    return fileMap;
  } catch (error) {
    console.error("Error resolving file IDs on client:", error);
    return {};
  }
}

/**
 * Deeply resolve GIDs within an object or array
 */
function resolveGIDsDeeply(
  data: any,
  fileMap: Record<string, string | null>,
): any {
  if (typeof data === "string") {
    if (data.startsWith("gid://shopify/")) {
      return fileMap[data] || data;
    }
    return data;
  }

  if (Array.isArray(data)) {
    return data.map((item) => resolveGIDsDeeply(item, fileMap));
  }

  if (data !== null && typeof data === "object") {
    const resolved: any = {};
    for (const [key, value] of Object.entries(data)) {
      resolved[key] = resolveGIDsDeeply(value, fileMap);
    }
    return resolved;
  }

  return data;
}

/**
 * Parse metaobject fields into a key-value object
 */
function parseMetaobjectFieldsClient(
  fields: any[],
  fileMap: Record<string, string | null> = {},
): Record<string, any> {
  const fieldMap: Record<string, any> = {};

  for (const field of fields) {
    let parsedValue: any;

    try {
      parsedValue = JSON.parse(field.value);
    } catch {
      parsedValue = field.value;
    }

    if (field.reference) {
      if (field.reference.fields) {
        fieldMap[field.key] = parseMetaobjectFieldsClient(
          field.reference.fields,
          fileMap,
        );
      } else if (field.reference.image) {
        fieldMap[field.key] = field.reference.image.url;
      } else if (field.reference.url) {
        fieldMap[field.key] = field.reference.url;
      } else {
        fieldMap[field.key] = resolveGIDsDeeply(parsedValue, fileMap);
      }
    } else if (
      field.references &&
      field.references.edges &&
      field.references.edges.length > 0
    ) {
      fieldMap[field.key] = field.references.edges.map((edge: any) => {
        if (edge.node.fields) {
          return parseMetaobjectFieldsClient(edge.node.fields, fileMap);
        } else if (edge.node.image) {
          return edge.node.image.url;
        } else if (edge.node.url) {
          return edge.node.url;
        } else {
          return resolveGIDsDeeply(edge.node, fileMap);
        }
      });
    } else {
      fieldMap[field.key] = resolveGIDsDeeply(parsedValue, fileMap);
    }
  }

  return fieldMap;
}

/**
 * Recursively extract all Shopify GIDs from fields
 */
function extractGIDsRecursive(data: any, gids: Set<string>): void {
  if (typeof data === "string" && data.startsWith("gid://shopify/")) {
    gids.add(data);
    return;
  }

  if (Array.isArray(data)) {
    data.forEach((item) => extractGIDsRecursive(item, gids));
    return;
  }

  if (data !== null && typeof data === "object") {
    // If it's a field object, check value and sub-structures
    if ("value" in data) {
      extractGIDsRecursive(data.value, gids);
      // Also try parsing value if it might be JSON
      try {
        const parsed = JSON.parse(data.value);
        extractGIDsRecursive(parsed, gids);
      } catch {}
    }

    // Check references
    if (data.reference?.fields)
      extractGIDsRecursive(data.reference.fields, gids);
    if (data.references?.edges) {
      data.references.edges.forEach((edge: any) => {
        if (edge.node?.fields) extractGIDsRecursive(edge.node.fields, gids);
        if (edge.node?.id && edge.node.id.startsWith("gid://shopify/"))
          gids.add(edge.node.id);
      });
    }

    // General object traversal
    for (const value of Object.values(data)) {
      extractGIDsRecursive(value, gids);
    }
  }
}

/**
 * Normalizes an image source, ensuring GIDs are resolved to URLs if possible.
 */
export function normalizeImageSrc(src: any): string {
  if (!src) return "";
  if (typeof src === "string") {
    if (src.startsWith("gid://")) {
      // If it's still a GID, it means it wasn't resolved by resolveFileIdsClient
      // or it was found in a place where we didn't expect it.
      console.warn(`[normalizeImageSrc] Unresolved GID found: ${src}`);
      return "";
    }
    return src;
  }
  if (src.image?.url) return src.image.url;
  if (src.url) return src.url;
  return "";
}

import { cache } from "react";

export const getMetaobjectClient = cache(async function getMetaobjectClient(
  handle: string,
  type: string,
): Promise<any> {
  const res = await shopifyClientFetch<any>({
    query: getMetaobjectByHandleQuery,
    variables: {
      handle,
      type,
    },
    revalidate: 86400,
    tags: ["metaobjects", `metaobject:${type}`, `metaobject:${type}:${handle}`],
  });

  const metaobject = res.body.data?.metaobject;
  if (!metaobject) return null;

  const gids = new Set<string>();
  extractGIDsRecursive(metaobject.fields, gids);

  const fileMap = await resolveFileIdsClient(Array.from(gids));
  return {
    id: metaobject.id,
    handle: metaobject.handle,
    type: metaobject.type,
    ...parseMetaobjectFieldsClient(metaobject.fields, fileMap),
  };
});

export const getMetaobjectsByIdsClient = cache(
  async function getMetaobjectsByIdsClient(ids: string[]): Promise<any[]> {
    const res = await shopifyClientFetch<any>({
      query: getMetaobjectsByIdsQuery,
      variables: {
        ids,
      },
      revalidate: 86400,
      tags: ["metaobjects"],
    });

    const nodes = res.body.data?.nodes || [];
    const validNodes = nodes.filter(Boolean);

    const gids = new Set<string>();
    validNodes.forEach((node: any) => extractGIDsRecursive(node.fields, gids));

    const fileMap = await resolveFileIdsClient(Array.from(gids));
    return validNodes.map((node: any) => ({
      id: node.id,
      handle: node.handle,
      type: node.type,
      ...parseMetaobjectFieldsClient(node.fields, fileMap),
    }));
  },
);

export const getMetaobjectsByTypeClient = cache(
  async function getMetaobjectsByTypeClient(
    type: string,
    first: number = 250,
  ): Promise<any[]> {
    const res = await shopifyClientFetch<any>({
      query: getMetaobjectsByTypeQuery,
      variables: {
        type,
        first,
      },
      revalidate: 86400,
      tags: ["metaobjects", `metaobject:${type}`],
    });

    const edges = res.body.data?.metaobjects?.edges || [];
    const nodes = edges.map((edge: any) => edge.node);

    const gids = new Set<string>();
    nodes.forEach((node: any) => extractGIDsRecursive(node.fields, gids));

    const fileMap = await resolveFileIdsClient(Array.from(gids));
    return nodes.map((node: any) => ({
      id: node.id,
      handle: node.handle,
      type: node.type,
      ...parseMetaobjectFieldsClient(node.fields, fileMap),
    }));
  },
);
